import React, { useState, useEffect } from "react";
import { Input, Button, Table, Modal, Form, Space } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";


import axios from "axios";

const { TextArea } = Input;
interface User {
  key: number;
  _id?: string;
  heading: string;
  subHeading: string;
  courses?: { name: string; description: string; category: string }[];
  welcomeText?:string;
  aboutHeading?: string;
  aboutSubHeading?: string;
  keywords?: string;
  testimonials?: string; 
  testimonialsTitle?: string;
}

const Home = () => {
  const [form] = Form.useForm();
  const [MetaData, setMetaData] = useState<User[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editUserIndex, setEditUserIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`https://service.ireedindia.com/v1/find-home`, {
            method: 'POST'
        });
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const responseData = await response.json();
        console.log("Fetched data:", responseData.data);
        setMetaData(responseData.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const onFinish = async (values: any) => {
    try {
      if (editUserIndex !== null) {
        const response = await axios.patch(
          `https://service.ireedindia.com/v1/home-edit/${editUserIndex}`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (response.status !== 200) {
          throw new Error("Failed to update user");
        }
        const updatedMetaData = MetaData.map((item) =>
          item.key === editUserIndex ? { ...item, ...values } : item
        );
        setMetaData(updatedMetaData);
      } else {
        await handleAdd(values);
      }
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error adding or updating user:", error);
    }
  };

  const handleAdd = async (values: any) => {
    try {
      const response = await axios.post(
        `https://service.ireedindia.com/v1/create-home`,
        values,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 201) {
        const newMetaData = [...MetaData, response.data]; // Assuming response.data contains the newly added data
        setMetaData(newMetaData);
      } else {
        throw new Error("Failed to add new record");
      }
    } catch (error) {
      console.error("Error adding new record:", error);
    }
  };

  const handleEdit = (record: User) => {
    form.setFieldsValue(record);
    setIsModalVisible(true);
    setEditUserIndex(record.key ?? record._id ?? null);
  };

  const handleDelete = async (record: User) => {
    try {
      const deleteKey = record.key || record._id;
      if (!deleteKey) {
        throw new Error("Record key is undefined");
      }
      const response = await axios.delete(
        `https://service.ireedindia.com/v1/home-delete/${deleteKey}`
      );
      if (response.status === 200) {
        const updatedMetaData = MetaData.filter((item) => item.key !== deleteKey);
        setMetaData(updatedMetaData);
      } else {
        throw new Error("Failed to delete record");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
    }
  };

  const successColumns = [
    {
      title: "Page Heading",
      dataIndex: "heading",
      key: "heading",
    },
    {
      title: "Sub Heading",
      dataIndex: "subHeading",
      key: "subHeading",
    },
    {
      title: "Courses",
      dataIndex: "courses",
      key: "courses",
      render: (courses: { name: string; description: string; category: string }[]) => (
        <>
          {courses.map((course, index) => (
            <div key={index}>
              <p><strong>Name:</strong> {course.name}</p>
              <p><strong>Description:</strong> {course.description}</p>
              <p><strong>Category:</strong> {course.category}</p>
            </div>
          ))}
        </>
      ),
    },
    {
        title: "Welcome Text", // Change this line
        dataIndex: "welcomeText",
        key: "welcomeText",
      },
    {
      title: "About Heading",
      dataIndex: "aboutHeading",
      key: "aboutHeading",
    },
    {
      title: "About Sub Heading",
      dataIndex: "aboutSubHeading",
      key: "aboutSubHeading",
    },
    {
      title: "Keywords",
      dataIndex: "keywords",
      key: "keywords",
    },
    {
      title: "Testimonials",
      dataIndex: "testimonials",
      key: "testimonials",
    },
    {
      title: "Testimonials Title",
      dataIndex: "testimonialsTitle",
      key: "testimonialsTitle",
    },
    {
      title: "Action",
      key: "action",
      render: (text: any, record: User) => (
        <span>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </span>
      ),
    },
  ];
  

  return (
    <div>
      <h2>Meta Data</h2>
      <Button type="primary" onClick={() => setIsModalVisible(true)}>
        Add Data
      </Button>
      <Table columns={successColumns} dataSource={MetaData} />
      <Modal
  title={editUserIndex !== null ? "Edit User" : "Add User"}
  visible={isModalVisible}
  onCancel={() => {
    form.resetFields();
    setIsModalVisible(false);
    setEditUserIndex(null);
  }}
  footer={null}
>
  <Form
    form={form}
    name="users"
    onFinish={onFinish}
    labelCol={{ span: 8 }}
    wrapperCol={{ span: 16 }}
  >
    {/* Page Heading */}
    <Form.Item
      label="Page Heading"
      name="heading"
      rules={[{  message: "Please input the page heading!" }]}
    >
      <Input />
    </Form.Item>

    {/* Sub Heading */}
    <Form.Item
      label="Sub Heading"
      name="subHeading"
      rules={[{ message: "Please input the sub heading!" }]}
    >
      <TextArea />
    </Form.Item>

    {/* Courses */}
    <Form.List name="courses">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
              {/* Course Name */}
              <Form.Item
                {...restField}
                name={[name, 'name']}
                rules={[{  message: 'Missing course name' }]}
              >
                <Input placeholder="Course Name" />
              </Form.Item>

              {/* Course Description */}
              <Form.Item
                {...restField}
                name={[name, 'description']}
                rules={[{  message: 'Missing course description' }]}
              >
                <Input.TextArea placeholder="Course Description" />
              </Form.Item>

              {/* Course Category */}
              <Form.Item
                {...restField}
                name={[name, 'category']}
                rules={[{  message: 'Missing course category' }]}
              >
                <Input placeholder="Course Category" />
              </Form.Item>

              {/* Delete Course Button */}
              <DeleteOutlined onClick={() => remove(name)} />
            </Space>
          ))}

          {/* Add Course Button */}
          <Form.Item>
            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
              Add Course
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>

    {/* About Heading */}
    <Form.Item
      label=" welcome Text"
      name=" welcomeText"
    >
      <Input />
    </Form.Item>
    <Form.Item
      label="About Heading"
      name="aboutHeading"
    >
      <Input />
    </Form.Item>

    {/* About Sub Heading */}
    <Form.Item
      label="About Sub Heading"
      name="aboutSubHeading"
    >
      <Input />
    </Form.Item>

    {/* Keywords */}
    <Form.Item
      label="Keywords"
      name="keywords"
    >
      <Input />
    </Form.Item>

    {/* Testimonials */}
    <Form.Item
      label="Testimonials"
      name="testimonials"
    >
      <TextArea />
    </Form.Item>

    {/* Testimonials Title */}
    <Form.Item
      label="Testimonials Title"
      name="testimonialsTitle"
    >
      <Input />
    </Form.Item>

    {/* Submit Button */}
    <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
      <Button type="primary" htmlType="submit">
        {editUserIndex !== null ? "Update" : "Add"}
      </Button>
    </Form.Item>
  </Form>
</Modal>

    </div>
  );
};

export default Home;
